import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { schedulesApi, queryKeys, StreamSchedule } from '@/lib/api';

interface ScheduleFormModalProps {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  facilityTimezone?: string; // IANA timezone, e.g. "America/Chicago"
  schedule?: StreamSchedule;
  sheetNames?: string; // facility's sheet names, JSON array string
  pausedSheets?: string; // out-of-service sheet names, JSON array string
}

// Used only when the facility has no sheet names configured.
const FALLBACK_SHEETS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface FormData {
  eventName: string;
  sheetId: string;
  team1Name: string;
  team2Name: string;
  scheduledStart: string;
  scheduledEnd: string;
  notes: string;
  obsSceneName: string;
}

const defaultForm: FormData = {
  eventName: '',
  sheetId: 'A',
  team1Name: '',
  team2Name: '',
  scheduledStart: '',
  scheduledEnd: '',
  notes: '',
  obsSceneName: '',
};

/**
 * Convert a UTC ISO string to a datetime-local string in the given IANA timezone.
 * Falls back to browser-local time if timezone is not provided.
 */
function toFacilityDatetimeString(isoString: string, timezone?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (!timezone) {
    // fallback: browser local time
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
    const hour = get('hour') === '24' ? '00' : get('hour'); // midnight edge case
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
  } catch {
    // If timezone is invalid, fall back to ISO local representation
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}

export default function ScheduleFormModal({ open, onClose, facilityId, facilityTimezone, schedule, sheetNames, pausedSheets }: ScheduleFormModalProps) {
  const queryClient = useQueryClient();
  // The agent matches games to streams by exact sheet name, so offer the
  // facility's own names. A hard-coded A–F list let Sault Ste Marie (sheets 1–8)
  // enter games that could never start (2026-10-28). Paused (out-of-service)
  // sheets are left out too: the agent is never sent games on them.
  const facilitySheets: string[] = React.useMemo(() => {
    const parse = (s?: string): string[] => {
      try { const v = JSON.parse(s || '[]'); return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
    };
    const names = parse(sheetNames);
    const paused = new Set(parse(pausedSheets));
    const usable = (names.length > 0 ? names : FALLBACK_SHEETS).filter(n => !paused.has(n));
    return usable.length > 0 ? usable : FALLBACK_SHEETS;
  }, [sheetNames, pausedSheets]);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [error, setError] = useState<string | null>(null);
  const [conflictNote, setConflictNote] = useState<string | null>(null);

  const isEdit = !!schedule;

  useEffect(() => {
    if (open) {
      if (schedule) {
        setForm({
          eventName: schedule.eventName || '',
          sheetId: schedule.sheetId || facilitySheets[0]!,
          team1Name: schedule.team1Name || '',
          team2Name: schedule.team2Name || '',
          scheduledStart: toFacilityDatetimeString(schedule.scheduledStart, facilityTimezone),
          scheduledEnd:   toFacilityDatetimeString(schedule.scheduledEnd,   facilityTimezone),
          notes: schedule.notes || '',
          obsSceneName: (schedule as any).obsSceneName || '',
        });
      } else {
        setForm({ ...defaultForm, sheetId: facilitySheets[0]! });
      }
      setError(null);
      setConflictNote(null);
    }
  }, [open, schedule, facilityTimezone]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => schedulesApi.create(data as Partial<StreamSchedule>),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      if (res?.conflictNote) {
        setConflictNote(res.conflictNote);
        // Don't close — show the note so the user sees the adjusted time
      } else {
        onClose();
      }
    },
    onError: (err: any) => setError(err?.message || 'Failed to create schedule'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => schedulesApi.update(schedule!.id, data as Partial<StreamSchedule>),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      if (res?.conflictNote) {
        setConflictNote(res.conflictNote);
      } else {
        onClose();
      }
    },
    onError: (err: any) => setError(err?.message || 'Failed to update schedule'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setConflictNote(null);

    if (!form.eventName.trim()) { setError('Event name is required'); return; }
    if (!form.scheduledStart) { setError('Start date/time is required'); return; }
    if (!form.scheduledEnd) { setError('End date/time is required'); return; }

    // Send the raw datetime-local string (YYYY-MM-DDTHH:MM).
    // The backend will interpret it in the facility's configured timezone.
    const payload: Record<string, unknown> = {
      facilityId,
      sheetId: form.sheetId,
      eventName: form.eventName.trim(),
      scheduledStart: form.scheduledStart,
      scheduledEnd:   form.scheduledEnd,
    };
    if (form.team1Name.trim()) payload['team1Name'] = form.team1Name.trim();
    if (form.team2Name.trim()) payload['team2Name'] = form.team2Name.trim();
    if (form.notes.trim()) payload['notes'] = form.notes.trim();
    if (form.obsSceneName.trim()) payload['obsSceneName'] = form.obsSceneName.trim();

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white text-gray-900 shadow-xl dark:bg-gray-900 dark:text-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Schedule' : 'New Schedule'}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}
          {conflictNote && (
            <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <strong>Schedule adjusted:</strong> {conflictNote}
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={onClose} className="text-xs underline">Close</button>
              </div>
            </div>
          )}

          {/* Event Name */}
          <div>
            <label className="mb-1 block text-sm font-medium">Event Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.eventName}
              onChange={e => handleChange('eventName', e.target.value)}
              placeholder="e.g. Morning Draw"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          {/* Sheet ID */}
          <div>
            <label className="mb-1 block text-sm font-medium">Sheet</label>
            <select
              value={form.sheetId}
              onChange={e => handleChange('sheetId', e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {facilitySheets.map(s => (
                <option key={s} value={s}>Sheet {s}</option>
              ))}
              {/* Keep an existing game's sheet visible even if it isn't configured */}
              {form.sheetId && !facilitySheets.includes(form.sheetId) && (
                <option value={form.sheetId}>Sheet {form.sheetId} (disabled or not configured — won't stream)</option>
              )}
            </select>
          </div>

          {/* Teams */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Team 1</label>
              <input
                type="text"
                value={form.team1Name}
                onChange={e => handleChange('team1Name', e.target.value)}
                placeholder="Team 1 name"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Team 2</label>
              <input
                type="text"
                value={form.team2Name}
                onChange={e => handleChange('team2Name', e.target.value)}
                placeholder="Team 2 name"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Start / End */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 -mb-1 flex items-center justify-between">
              <span className="text-sm font-medium">Times</span>
              {facilityTimezone && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Facility timezone: <span className="font-mono">{facilityTimezone}</span>
                </span>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Start <span className="text-red-500">*</span></label>
              <input
                type="datetime-local"
                value={form.scheduledStart}
                onChange={e => handleChange('scheduledStart', e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">End <span className="text-red-500">*</span></label>
              <input
                type="datetime-local"
                value={form.scheduledEnd}
                onChange={e => handleChange('scheduledEnd', e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

          {/* OBS Scene */}
          <div>
            <label className="mb-1 block text-sm font-medium">OBS Scene Name</label>
            <input
              type="text"
              value={form.obsSceneName}
              onChange={e => handleChange('obsSceneName', e.target.value)}
              placeholder="e.g. Sheet A Live"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-sm font-medium">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Optional notes..."
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
}
